import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { useTelemetryStore } from "@/store/telemetryStore";
import {
  ChangeType,
  type RedoChange,
  type UndoChange,
} from "@/store/zustand/changeLedgerTypes";
import {
  APPLIED_BUT_NOT_CONFIRMED_BY_API_CONSTANT,
  FAILED_CONSTANT,
  useDesignCommandManager,
} from "@/store/zustand/designCommandManagerStore";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { track } from "@/utils/events/tracker";
import {
  decompressHtml,
  decompressNavTree,
  rebuildLookupsFromNavTree,
} from "@/utils/navTreeCompression";
import { filterOutNotNeededNodes } from "@/utils/navTreeUtils";
import type { NavTreeNode, Storyboard } from "@/utils/types";
import type { ApplyInstantUpdates } from "../Storyboard/StaticIframe/hooks/useApplyInstantUpdates";
import {
  getEmptyLookups,
  isNavTreeEmpty,
  updateAllStoreLookups,
} from "../Storyboard/StaticIframe/utils/navTreeLookupUtils";
import {
  combineShellAndBody,
  getFileKeyForStoryboard,
  getShellFromHtml,
  throttledUpdateSavedHtmlOnDB,
} from "./utils";
import { filterSerializable } from "@/utils/utils";

const getCodebaseIdsFromHtml = (html: string) => {
  const codebaseIdRegex =
    /tempo-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
  const codebaseIdMatches = html.match(codebaseIdRegex) || [];
  const codebaseIds = new Set<string>(codebaseIdMatches);
  return codebaseIds;
};

export const GLOBAL_DEV_MODE = false;

export interface IframeState {
  canvasId: string;
  id: string;
  status: "loading" | "ready" | "error" | "resetting";
  contentWindow: WindowProxy | null;
  refreshKey: number;
  clearRetriesTrigger: number;
  // Position properties for drive mode
  drivePosition?: {
    left: number;
    top: number;
    width: number;
    height: number;
    visible: boolean;
  };

  // If true - had already refreshed for an error, don't refresh again
  refreshedForError?: boolean;

  // If true - the iframe failed to load
  failedToLoad?: boolean;

  // Heartbeat tracking
  isNonResponsive?: boolean;
  heartbeatTimeoutId?: NodeJS.Timeout | null;
}

export interface StoryboardRequiredFields {
  // The only fields we need
  // TODO: Always get this fresh from the global store
  id: string;
  type: string;
  fullFilePath: string;
  storyComponentName?: string;
  name?: string;
}

interface RegisteredStoryboard {
  id: string;
  storyboard: StoryboardRequiredFields;
  lastRefreshTs: number;
  fileKey: string;

  // When force refreshing, show the manual loading state
  showManualLoading?: boolean;

  // Failed to load
  failedToLoad?: boolean;

  // Hot reload timestamp
  hotReloadTsAtLastRefresh: number | null;

  width: number;
  height: number;
  left: number;
  top: number;

  // After a code change that did not have an instant update, we need a fresh nav tree
  // before allowing the user to interact with the storyboard
  // The timestamp is the hot reload timestamp when the change was made
  needsFreshNavTreeTs?: number;
}

interface SavedStoryboardHtml {
  id: number;
  storyboard_id: string;
  canvas_id: string;
  html: string | null;
  created_at: string;
  last_frozen_at: string | null;
  saved_nav_tree: string | null;
}

interface ApplicationStoryboard {
  id: string;
  storyboard: StoryboardRequiredFields;
  canvasId: string;
  iframeId: string;
  devServerUrl: string;
  refreshKey: number;
  clearRetriesTrigger: number;
  showManualLoading?: boolean;
  contentWindow: WindowProxy | null;

  status: "loading" | "ready";

  // This is just the timestamp of the last refresh
  lastRefreshTs: number;

  // This is the timestamp of the hot reload ts from the use effect of the router
  hotReloadTsAtLastRefresh: number | null;

  width: number;
  height: number;
  left: number;
  top: number;

  // Failed to load
  failedToLoad?: boolean;

  // After a code change that did not have an instant update, we need a fresh nav tree
  // before allowing the user to interact with the storyboard
  // The timestamp is the hot reload timestamp when the change was made
  needsFreshNavTreeTs?: number;

  // If true - had already refreshed for an error, don't refresh again
  refreshedForError?: boolean;

  // Heartbeat tracking
  isNonResponsive?: boolean;
  heartbeatTimeoutId?: NodeJS.Timeout | null;
}

interface IframePoolStore {
  tempobookIframe: IframeState | null;

  // Registration system
  registeredStoryboards: Record<string, RegisteredStoryboard>;
  htmlForStoryboard: Record<string, string>; // Combined HTML for each storyboard (for Supabase)
  shellForStoryboard: Record<string, string>; // Shell HTML (html/head with empty body)
  bodyHtmlForStoryboard: Record<string, string>; // Body outerHTML

  // Application storyboards (separate from pool)
  applicationStoryboards: Record<string, ApplicationStoryboard>;

  // Unified storage for all storyboard contentWindows.
  // These are always from StaticIframeComponent which renders frozen HTML content,
  // NOT from live application iframes. This ensures we're working with static
  // snapshots for features like instance screenshots.
  storyboardIdToContentWindow: Record<string, WindowProxy>;

  applicationPreviewRequestTimeout: ReturnType<typeof setTimeout> | null;
  applicationPreviewNextTs: number;

  // Retry configuration
  maxRetries: number;
  baseRetryDelay: number;

  // Checking for hot reloads
  maxHotReloadTs: number | null;
  expectedMaxHotReloadTs: number | null;
  latestHtmlShell: string | null;

  hadEmptyNavTreeStoryboardIds: Set<string>;

  // Track which canvases have sent non-responsive events to avoid duplicates
  nonResponsiveEventsSentForCanvas: Set<string>;

  // Track panel resize state to temporarily disable iframe pointer events
  isPanelResizing: boolean;

  isCheckingDevServerHtmlErrors: boolean;
  setIsCheckingDevServerHtmlErrors: (isChecking: boolean) => void;

  // Panel resize state management
  setIsPanelResizing: (isResizing: boolean) => void;

  // Fullscreen mode - track which storyboard is currently fullscreen
  fullscreenStoryboardId: string | null;
  setFullscreenStoryboard: (storyboardId: string | null) => void;

  resetIframePoolStore: () => void;

  // Unified contentWindow storage for all storyboards
  setContentWindowForStoryboard: (
    storyboardId: string,
    contentWindow: WindowProxy | null,
  ) => void;

  // Actions
  initializeIframe: (canvasId: string) => void;
  setIframeContentWindow: (contentWindow: WindowProxy) => void;
  setIframeDrivePosition: (
    position:
      | {
          left: number;
          top: number;
          width: number;
          height: number;
          visible: boolean;
        }
      | undefined,
  ) => void;

  // Registration system
  registerStoryboard: (
    storyboardId: string,
    storyboard: StoryboardRequiredFields,
    canvasId: string,
    width: number,
    height: number,
    left: number,
    top: number,
  ) => void;
  unregisterStoryboard: (storyboardId: string) => void;
  setSavedStoryboardHtml: (
    htmlData: Record<string, SavedStoryboardHtml>,
  ) => void;
  setHtmlForStoryboard: (storyboardId: string, html: string) => void;

  // Controls for storyboard refresh
  notifyCodebaseIdsChanged: (
    codebaseIds: Set<string>,
    filepathsChanged: Set<string>,
    hadInstantUpdate: boolean,
    expectedHotReloadTs: number,
  ) => void;
  refreshStoryboard: (storyboardId: string, refreshForError?: boolean) => void; // Force refresh, load a new iframe window
  refreshAllStoryboards: () => void; // Force refresh, load a new iframe window

  // Process the new html
  _processNewHtml: (props: {
    canvasId: string;
    storyboard: Storyboard;
    shellHtml: string;
    bodyHtml: string;
    navTreeFromIframe?: NavTreeNode;
    applyInstantUpdate: (props: ApplyInstantUpdates) => void;
    skipUpdateNavTree?: boolean;
  }) => Promise<{
    isEmpty?: boolean;
  }>;

  // Callbacks from iframe
  handleApplicationIframeReady: (iframeId: string) => void;
  requestNavTreeForAllStoryboards: () => void;
  handleIframeHotReloadTs: (hotReloadTs: number) => void;
  requestNavTreeForApplicationStoryboard: (storyboardId: string) => void;
  markIframeFailedToLoad: () => void;

  shouldIgnoreUpdatesForStoryboard: (
    storyboardId: string,
    currentMaxHotReloadTs: number,
  ) => boolean;
  handleNavTreeReceived: (
    storyboard: Storyboard,
    shellHtml: string,
    bodyHtml: string,
    canvasId: string,
    applyInstantUpdate: (props: ApplyInstantUpdates) => void,
    navTree?: NavTreeNode | null,
    overwriteHotReloadTs?: number,
  ) => void;
  handleNewOuterHtmlOnly: (
    storyboard: Storyboard,
    shellHtml: string,
    bodyHtml: string,
    canvasId: string,
    applyInstantUpdate: (props: ApplyInstantUpdates) => void,
    overwriteHotReloadTs?: number,
  ) => void;

  runNavigationAndAskForNavTrees: () => void;
  resetIframe: (hardReset?: boolean) => void;
  retryIframe: () => void;
  setShowLoading: (storyboardId: string, showLoading: boolean) => void;
  sendMessageToApplicationStoryboard: (
    storyboardId: string,
    type: string,
    payload: any,
    callback?: () => void,
  ) => void;

  // Application storyboard specific methods
  setApplicationStoryboardContentWindow: (
    storyboardId: string,
    contentWindow: WindowProxy | null,
  ) => void;
  resetApplicationStoryboard: (storyboardId: string) => void;
  markApplicationStoryboardAsFailedToLoad: (storyboardId: string) => void;
  retryApplicationIframe: (storyboardId: string) => void;

  updateStoryboardSize: (
    storyboardId: string,
    width: number,
    height: number,
    left: number,
    top: number,
  ) => void;

  updateRegisteredStoryboardData: (
    storyboardId: string,
    storyboard: StoryboardRequiredFields,
  ) => void;

  storyboardScrollPositions: Record<
    string,
    Record<string, { top: number; left: number }>
  >;
  setStoryboardScrollPosition: (
    storyboardId: string,
    domUniquePath: string,
    top: number,
    left: number,
  ) => void;

  ignoreUpdatesForStoryboards: Set<string>;
  setIgnoreUpdatesForStoryboards: (storyboardId: string) => void;
  unsetIgnoreUpdatesForStoryboards: (storyboardId: string) => void;

  // Heartbeat methods
  handleHeartbeatReceived: (iframeIdOrAppStoryboardId?: string) => void;
  markStoryboardAsNonResponsive: (storyboardId: string) => void;
  clearNonResponsiveStatus: (storyboardId: string) => void;

  setLatestHtmlShell: (htmlShell: string) => void;
}

// 10 seconds for hearbeat timeout. Hearbeat interval is 3 seconds, so we should get 9 missed heartbeats before timing out
export const IFRAME_HEARTBEAT_TIMEOUT_MS = 30000;

const clearHealthcheckTimeout = (iframe: IframeState | null) => {
  if (iframe?.heartbeatTimeoutId) {
    clearTimeout(iframe.heartbeatTimeoutId);
  }
};

export const useIframePoolStore = create<IframePoolStore>((set, get) => ({
  tempobookIframe: null,
  registeredStoryboards: {},

  htmlForStoryboard: {},
  shellForStoryboard: {},
  bodyHtmlForStoryboard: {},
  applicationStoryboards: {},
  storyboardIdToContentWindow: {},
  applicationPreviewRequestTimeout: null,
  applicationPreviewNextTs: 0,
  maxRetries: 5,
  baseRetryDelay: 3 * 1000,
  maxHotReloadTs: null,
  expectedMaxHotReloadTs: null,
  latestHtmlShell: null,
  lastProcessedHotReloadTsForStoryboard: {},
  hadEmptyNavTreeStoryboardIds: new Set(),
  nonResponsiveEventsSentForCanvas: new Set(),

  isPanelResizing: false,

  isCheckingDevServerHtmlErrors: false,
  setIsCheckingDevServerHtmlErrors: (isChecking: boolean) => {
    set({ isCheckingDevServerHtmlErrors: isChecking });
  },

  setIsPanelResizing: (isResizing: boolean) => {
    set({ isPanelResizing: isResizing });
  },

  fullscreenStoryboardId: null,
  setFullscreenStoryboard: (storyboardId: string | null) => {
    set({ fullscreenStoryboardId: storyboardId });
  },

  setContentWindowForStoryboard: (
    storyboardId: string,
    contentWindow: WindowProxy | null,
  ) => {
    if (contentWindow) {
      set((state) => ({
        storyboardIdToContentWindow: {
          ...state.storyboardIdToContentWindow,
          [storyboardId]: contentWindow,
        },
      }));
    } else {
      set((state) => {
        const { [storyboardId]: _, ...rest } =
          state.storyboardIdToContentWindow;
        return { storyboardIdToContentWindow: rest };
      });
    }
  },

  resetIframePoolStore: () => {
    const state = get();

    // Clear heartbeat timeout for the tempobook iframe
    clearHealthcheckTimeout(state.tempobookIframe);

    // Clear any existing heartbeat timeouts for application storyboards
    for (const appStoryboard of Object.values(state.applicationStoryboards)) {
      if (appStoryboard.heartbeatTimeoutId) {
        clearTimeout(appStoryboard.heartbeatTimeoutId);
      }
    }

    set({
      tempobookIframe: null,
      registeredStoryboards: {},
      htmlForStoryboard: {},
      shellForStoryboard: {},
      bodyHtmlForStoryboard: {},
      applicationStoryboards: {},
      storyboardIdToContentWindow: {},
      maxRetries: 5,
      baseRetryDelay: 3 * 1000,
      maxHotReloadTs: null,
      latestHtmlShell: null,
      hadEmptyNavTreeStoryboardIds: new Set(),
    });
  },

  initializeIframe: (canvasId) => {
    // Clear any timeouts for the existing tempobook iframe
    const state = get();
    clearHealthcheckTimeout(state.tempobookIframe);

    set({
      tempobookIframe: {
        canvasId,
        id: uuidv4(),
        status: "loading",
        contentWindow: null,
        refreshKey: 0,
        clearRetriesTrigger: 0,
      },
    });
  },

  setIframeContentWindow: (contentWindow) => {
    set((state) => ({
      tempobookIframe: state.tempobookIframe
        ? { ...state.tempobookIframe, contentWindow }
        : null,
    }));

    // Process any pending requests because the iframe could be ready now
    get().runNavigationAndAskForNavTrees();
  },

  setIframeDrivePosition: (position) => {
    set((state) => ({
      tempobookIframe: state.tempobookIframe
        ? { ...state.tempobookIframe, drivePosition: position }
        : null,
    }));
  },

  // Registration system methods
  registerStoryboard: (
    storyboardId,
    storyboard,
    canvasId,
    width,
    height,
    left,
    top,
  ) => {
    // Handle application storyboards separately
    if (storyboard.type === STORYBOARD_TYPES.APPLICATION) {
      // Create a dedicated iframe for this application storyboard
      const iframeId = `app-${storyboardId}`;

      if (get().applicationStoryboards[storyboardId]) {
        return;
      }

      set((state) => ({
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [storyboardId]: {
            id: storyboardId,
            storyboard,
            canvasId,
            iframeId,
            devServerUrl: "", // Will be set by the component
            refreshKey: 0,
            contentWindow: null,
            hotReloadTsAtLastRefresh: null,
            lastRefreshTs: 0,
            width,
            height,
            left,
            top,
            status: "loading",
            clearRetriesTrigger: 0,
            needsFreshNavTreeTs: undefined,
          },
        },
      }));

      return;
    }

    if (get().registeredStoryboards[storyboardId]) {
      return;
    }

    // Regular storyboard registration
    set((state) => ({
      registeredStoryboards: {
        ...state.registeredStoryboards,
        [storyboardId]: {
          id: storyboardId,
          fileKey: getFileKeyForStoryboard(storyboard as Storyboard, canvasId),
          storyboard,
          lastRefreshTs: 0,
          hotReloadTsAtLastRefresh: null,
          width,
          height,
          left,
          top,
          needsFreshNavTreeTs: undefined,
        },
      },
    }));

    if (get().htmlForStoryboard[storyboardId]) {
      return;
    }

    // Request the HTML only if there is none from the DB
    get().runNavigationAndAskForNavTrees();
  },

  updateStoryboardSize: (
    storyboardId: string,
    width: number,
    height: number,
    left: number,
    top: number,
  ) => {
    const state = get();
    if (state.applicationStoryboards[storyboardId]) {
      set((state) => ({
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [storyboardId]: {
            ...state.applicationStoryboards[storyboardId],
            width,
            height,
            left,
            top,
          },
        },
      }));
    }

    if (state.registeredStoryboards[storyboardId]) {
      set((state) => ({
        registeredStoryboards: {
          ...state.registeredStoryboards,
          [storyboardId]: {
            ...state.registeredStoryboards[storyboardId],
            width,
            height,
            left,
            top,
          },
        },
      }));
    }
  },

  updateRegisteredStoryboardData: (
    storyboardId: string,
    storyboard: StoryboardRequiredFields,
  ) => {
    const state = get();
    if (state.registeredStoryboards[storyboardId]) {
      set((state) => ({
        registeredStoryboards: {
          ...state.registeredStoryboards,
          [storyboardId]: {
            ...state.registeredStoryboards[storyboardId],
            storyboard,
          },
        },
      }));
    }
  },

  unregisterStoryboard: (storyboardId) => {
    set((state) => {
      const newRegisteredStoryboards = { ...state.registeredStoryboards };
      delete newRegisteredStoryboards[storyboardId];

      const newApplicationStoryboards = { ...state.applicationStoryboards };
      const appStoryboard = newApplicationStoryboards[storyboardId];

      // Clear heartbeat timeout for application storyboard if it exists
      if (appStoryboard?.heartbeatTimeoutId) {
        clearTimeout(appStoryboard.heartbeatTimeoutId);
      }

      delete newApplicationStoryboards[storyboardId];

      return {
        registeredStoryboards: newRegisteredStoryboards,
        applicationStoryboards: newApplicationStoryboards,
      };
    });
  },

  setSavedStoryboardHtml: (idToHtmlData) => {
    // Convert array of saved HTML objects to a map keyed by storyboard_id
    const { htmlForStoryboard } = get();
    const newHtmlForStoryboard: Record<string, string> = {
      ...htmlForStoryboard,
    };

    let setHtmlShell = false;
    Object.values(idToHtmlData).forEach((savedHtmlObj) => {
      if (savedHtmlObj.storyboard_id && savedHtmlObj.html) {
        // Decompress HTML if it's compressed, otherwise use as-is
        const decompressedHtml = decompressHtml(savedHtmlObj.html);
        if (decompressedHtml) {
          newHtmlForStoryboard[savedHtmlObj.storyboard_id] = decompressedHtml;
        }
      }

      // Parse codebase IDs from decompressed HTML
      const htmlForParsing = newHtmlForStoryboard[savedHtmlObj.storyboard_id];
      if (htmlForParsing) {
        const codebaseIds = getCodebaseIdsFromHtml(htmlForParsing);
        useCanvasStore
          .getState()
          .setStoryboardIdToCodebaseIds(
            savedHtmlObj.storyboard_id,
            codebaseIds,
          );

        if (!setHtmlShell) {
          setHtmlShell = true;
          const htmlShell = getShellFromHtml(htmlForParsing);
          set({ latestHtmlShell: htmlShell });
        }
      }

      // Decompress and restore nav tree if available
      if (savedHtmlObj.saved_nav_tree) {
        const navTree = decompressNavTree(savedHtmlObj.saved_nav_tree);
        if (navTree) {
          const storyboardId = savedHtmlObj.storyboard_id;

          // Set the nav tree in the canvas store
          useCanvasStore
            .getState()
            .setNavTreeForStoryboard(storyboardId, navTree);

          // Rebuild and set all the lookups
          const lookups = rebuildLookupsFromNavTree(navTree, storyboardId);

          useCanvasStore
            .getState()
            .setElementKeyToNavNodeLookup(
              storyboardId,
              lookups.elementKeyLookup,
            );

          useCanvasStore
            .getState()
            .setDomUniquePathElementKeyLookup(
              storyboardId,
              lookups.domUniquePathElementKeyLookup,
            );

          useCanvasStore
            .getState()
            .setCodebaseIdToNavNodeLookup(
              storyboardId,
              lookups.codebaseIdLookup,
            );

          // Track cached nav tree as received for telemetry
          useTelemetryStore.getState().markNavTreeReceived({
            canvasId: savedHtmlObj.canvas_id,
            cached: true,
          });
        }
      }
    });

    set({ htmlForStoryboard: newHtmlForStoryboard });
  },

  setHtmlForStoryboard: (storyboardId: string, html: string) => {
    set((state) => ({
      htmlForStoryboard: {
        ...state.htmlForStoryboard,
        [storyboardId]: html,
      },
    }));
  },

  notifyCodebaseIdsChanged: (
    codebaseIds,
    filepathsChanged,
    hadInstantUpdate,
    expectedHotReloadTs,
  ) => {
    const state = get();
    const storyboardIdToCodebaseIds =
      useCanvasStore.getState().storyboardIdToCodebaseIds;
    const storyboardIdToFilepaths =
      useCanvasStore.getState().storyboardIdToFilepaths;

    // For application storyboards, no need to request html but still mark the ones
    // that need a fresh nav tree
    if (!hadInstantUpdate) {
      Object.values(state.applicationStoryboards).forEach((storyboard) => {
        const storyboardCodebaseIds = storyboardIdToCodebaseIds[storyboard.id];
        const storyboardFilepaths = storyboardIdToFilepaths[storyboard.id];

        if (!storyboardCodebaseIds && !storyboardFilepaths) {
          return;
        }

        // Check if this storyboard's codebase IDs intersect with changed ones
        const hasChangedCodebase = Array.from(codebaseIds).some((codebaseId) =>
          storyboardCodebaseIds.has(codebaseId),
        );

        const hasChangedFilepath = Array.from(filepathsChanged).some(
          (filepath) => storyboardFilepaths.has(filepath),
        );

        if (hasChangedCodebase || hasChangedFilepath) {
          set((state) => ({
            applicationStoryboards: {
              ...state.applicationStoryboards,
              [storyboard.id]: {
                ...storyboard,
                needsFreshNavTreeTs: expectedHotReloadTs,
              },
            },
          }));
        }
      });
    }

    // Find registered storyboards that contain any of the changed codebase IDs
    let anyStoryboardsChanged = false;
    Object.values(state.registeredStoryboards).forEach((storyboard) => {
      const storyboardCodebaseIds = storyboardIdToCodebaseIds[storyboard.id];
      const storyboardFilepaths = storyboardIdToFilepaths[storyboard.id];

      if (!storyboardCodebaseIds && !storyboardFilepaths) {
        return;
      }

      // Check if this storyboard's codebase IDs intersect with changed ones
      const hasChangedCodebase = Array.from(codebaseIds).some((codebaseId) =>
        storyboardCodebaseIds.has(codebaseId),
      );

      const hasChangedFilepath = Array.from(filepathsChanged).some((filepath) =>
        storyboardFilepaths.has(filepath),
      );

      // Ask for the html for this storyboard
      if (hasChangedCodebase || hasChangedFilepath) {
        // If there was no instant update, we need a fresh nav tree
        if (!hadInstantUpdate) {
          set((state) => ({
            registeredStoryboards: {
              ...state.registeredStoryboards,
              [storyboard.id]: {
                ...storyboard,
                needsFreshNavTreeTs: expectedHotReloadTs,
              },
            },
          }));
        }

        anyStoryboardsChanged = true;
      }
    });

    if (anyStoryboardsChanged) {
      get().runNavigationAndAskForNavTrees();
    }

    // Reset the iframes in the pool if a hot reload doesn't happen in 5 seconds
    if (!hadInstantUpdate) {
      const currentHotReloadTs = get().maxHotReloadTs || 0;
      const expectedMaxHotReloadTs = Math.max(
        get().expectedMaxHotReloadTs || 0,
        expectedHotReloadTs || 0,
      );

      if (expectedMaxHotReloadTs > currentHotReloadTs) {
        set({ expectedMaxHotReloadTs });
      }

      setTimeout(() => {
        const newMaxHotReloadTs = get().maxHotReloadTs;
        if (!newMaxHotReloadTs || newMaxHotReloadTs < expectedHotReloadTs) {
          // Reset the iframe
          get().resetIframe(true);
        }
      }, 5 * 1000);
    }

    if (get().applicationPreviewRequestTimeout == null) {
      const timeout =
        new URL(window.location.href).searchParams["generate-prd"] === "t"
          ? 5 * 1000
          : 60 * 1000;
      set({
        applicationPreviewRequestTimeout: setTimeout(
          () => {
            Object.values(state.applicationStoryboards).forEach(
              (storyboard) => {
                const port =
                  useIframeStore.getState().applicationStoryboardPorts[
                    storyboard.iframeId
                  ];
                if (port) {
                  port.postMessage({
                    id: uuidv4(),
                    type: "executeFunction",
                    payload: {
                      functionName: "captureProjectPreview",
                    },
                  });
                }
              },
            );
            set({
              applicationPreviewRequestTimeout: null,
              applicationPreviewNextTs: Date.now() + timeout,
            });
          },
          Math.max(5 * 1000, get().applicationPreviewNextTs - Date.now()),
        ),
      });
    }
  },

  runNavigationAndAskForNavTrees: () => {
    const state = get();

    // Check if tempobook iframe is available
    const tempobookIframe = state.tempobookIframe;
    if (
      !tempobookIframe ||
      tempobookIframe.status !== "ready" ||
      !tempobookIframe.contentWindow ||
      !tempobookIframe.canvasId
    ) {
      return;
    }

    const port = useIframeStore.getState().iframeMessagingPort;
    if (port) {
      port.postMessage({
        id: uuidv4(),
        type: "executeFunction",
        payload: {
          functionName: "buildAndSendNavTree",
        },
      });
    }

    const runNavigation = () => {
      const registeredStoryboard = state.registeredStoryboards;
      const allStoryboardIds = Object.keys(registeredStoryboard);

      if (!allStoryboardIds.length) {
        return;
      }

      // Notify tempobook of all the storyboards to render
      const fileKeys = allStoryboardIds.map(
        (storyboardId) => registeredStoryboard[storyboardId].fileKey,
      );

      if (fileKeys?.length) {
        const payload = {
          type: "TEMPOBOOK_NAVIGATE",
          payload: {
            fileKeys,
          },
        };

        if (tempobookIframe.contentWindow) {
          tempobookIframe.contentWindow.postMessage(payload, "*");
        }
      }
    };

    const askForNavTree = () => {
      const port = useIframeStore.getState().iframeMessagingPort;
      if (port) {
        port.postMessage({
          id: uuidv4(),
          type: "executeFunction",
          payload: {
            functionName: "buildAndSendNavTree",
          },
        });
      }
    };

    runNavigation();
    setTimeout(() => {
      askForNavTree();
    }, 10);
  },

  refreshStoryboard: (storyboardId: string, refreshForError?: boolean) => {
    const state = get();

    // Check if this is an application storyboard
    const appStoryboard = state.applicationStoryboards[storyboardId];
    if (appStoryboard) {
      if (refreshForError && appStoryboard.refreshedForError) {
        return;
      }

      // For application storyboards, increment the refreshKey and set showManualLoading
      set((state) => ({
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [storyboardId]: {
            ...state.applicationStoryboards[storyboardId],
            status: "loading",
            refreshKey:
              state.applicationStoryboards[storyboardId].refreshKey + 1,
            showManualLoading: true,
            contentWindow: null,
            failedToLoad: false,
            isNonResponsive: false, // Clear non-responsive status on refresh
            clearRetriesTrigger:
              state.applicationStoryboards[storyboardId].clearRetriesTrigger +
              1,
            refreshedForError: refreshForError,
          },
        },
      }));
      return;
    }

    // Regular storyboard refresh logic
    const tempobookIframe = state.tempobookIframe;

    if (refreshForError && tempobookIframe?.refreshedForError) {
      return;
    }

    // Update the storyboard iframe state to show manual loading
    set((state) => ({
      registeredStoryboards: {
        ...state.registeredStoryboards,
        [storyboardId]: {
          ...state.registeredStoryboards[storyboardId],
          showManualLoading: true,
        },
      },
    }));

    // Re-initialize the tempobook iframe and increment the refresh key
    if (tempobookIframe) {
      // Clear the retries for this iframe
      set((state) => ({
        tempobookIframe: state.tempobookIframe
          ? {
              ...state.tempobookIframe,
              clearRetriesTrigger:
                state.tempobookIframe.clearRetriesTrigger + 1,
              refreshedForError: refreshForError,
            }
          : null,
      }));

      get().resetIframe();
    }

    get().runNavigationAndAskForNavTrees();
  },

  refreshAllStoryboards: () => {
    const state = get();
    if (Object.keys(state.registeredStoryboards).length > 0) {
      Object.values(state.registeredStoryboards).forEach((storyboard) => {
        // Update the storyboard iframe state to show manual loading
        set((state) => ({
          registeredStoryboards: {
            ...state.registeredStoryboards,
            [storyboard.id]: {
              ...state.registeredStoryboards[storyboard.id],
              showManualLoading: true,
            },
          },
        }));
      });

      // Just pick one storyboard and refresh it as it triggeres a refresh for all non-application storyboards
      get().refreshStoryboard(Object.keys(state.registeredStoryboards)[0]);
    }

    Object.values(state.applicationStoryboards).forEach((storyboard) => {
      get().refreshStoryboard(storyboard.id);
    });
  },

  handleApplicationIframeReady: (iframeId: string) => {
    const state = get();
    const applicationStoryboard = Object.values(
      state.applicationStoryboards,
    ).find((storyboard) => storyboard.iframeId === iframeId);
    if (applicationStoryboard) {
      set({
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [applicationStoryboard.id]: {
            ...applicationStoryboard,
            status: "ready",
          },
        },
      });
    }
  },

  requestNavTreeForApplicationStoryboard: (storyboardId: string) => {
    const state = get();

    const appStoryboard = state.applicationStoryboards[storyboardId];
    if (!appStoryboard) {
      return;
    }

    const port =
      useIframeStore.getState().applicationStoryboardPorts[
        appStoryboard.iframeId
      ];
    if (port) {
      port.postMessage({
        id: uuidv4(),
        type: "executeFunction",
        payload: {
          functionName: "buildAndSendNavTree",
        },
      });
    }
  },

  requestNavTreeForAllStoryboards: () => {
    const state = get();

    get().runNavigationAndAskForNavTrees();

    // For application storyboards we can just request a new one
    Object.values(state.applicationStoryboards).forEach((storyboard) => {
      const port =
        useIframeStore.getState().applicationStoryboardPorts[
          storyboard.iframeId
        ];
      if (port) {
        port.postMessage({
          id: uuidv4(),
          type: "executeFunction",
          payload: {
            functionName: "buildAndSendNavTree",
          },
        });
      }
    });
  },

  handleIframeHotReloadTs: (hotReloadTs) => {
    const state = get();
    const tempobookIframe = state.tempobookIframe;

    // The backend notifies the hot reload periodically, if this changes the max hot reload ts,
    // it means this is a new hot reload
    const newLoad =
      tempobookIframe?.status !== "ready" ||
      !state.maxHotReloadTs ||
      state.maxHotReloadTs !== hotReloadTs;

    if (!newLoad) {
      return;
    }

    // Clear the timeout for the tempobook iframe
    clearHealthcheckTimeout(tempobookIframe);

    set({
      tempobookIframe: tempobookIframe
        ? {
            ...tempobookIframe,
            status: "ready",
          }
        : null,
    });

    const newHotReloadTs = Math.max(state.maxHotReloadTs || 0, hotReloadTs);

    set({ maxHotReloadTs: newHotReloadTs });

    get().requestNavTreeForAllStoryboards();
  },

  markIframeFailedToLoad: () => {
    const state = get();
    const tempobookIframe = state.tempobookIframe;

    if (!tempobookIframe) return;

    // Clear the timeout for the tempobook iframe
    clearHealthcheckTimeout(tempobookIframe);

    set({
      tempobookIframe: {
        ...tempobookIframe,
        status: "error",
        failedToLoad: true,
        clearRetriesTrigger: tempobookIframe.clearRetriesTrigger + 1,
      },
    });
  },

  markApplicationStoryboardAsFailedToLoad: (storyboardId: string) => {
    const state = get();
    const appStoryboard = state.applicationStoryboards[storyboardId];

    if (!appStoryboard) return;

    set((state) => ({
      applicationStoryboards: {
        ...state.applicationStoryboards,
        [storyboardId]: {
          ...appStoryboard,
          failedToLoad: true,
          clearRetriesTrigger:
            state.applicationStoryboards[storyboardId].clearRetriesTrigger + 1,
        },
      },
    }));
  },

  retryIframe: () => {
    const state = get();
    const tempobookIframe = state.tempobookIframe;

    if (!tempobookIframe) return;

    // Clear the timeout for the tempobook iframe
    clearHealthcheckTimeout(tempobookIframe);

    set({
      tempobookIframe: {
        ...tempobookIframe,
        status: "resetting",
        refreshKey: tempobookIframe.refreshKey + 1,
        contentWindow: null,
        isNonResponsive: false, // Clear non-responsive status on retry
        heartbeatTimeoutId: null, // Clear any existing timeout
      },
    });
  },

  resetIframe: (hardReset = false) => {
    // Clear the timeout for the tempobook iframe
    const state = get();
    clearHealthcheckTimeout(state.tempobookIframe);

    if (!state.tempobookIframe) return;

    set({
      tempobookIframe: {
        ...state.tempobookIframe,
        status: hardReset ? "loading" : "resetting",
        failedToLoad: false,
        refreshKey: state.tempobookIframe.refreshKey + 1,
        contentWindow: null,
        isNonResponsive: false, // Clear non-responsive status on reset
        heartbeatTimeoutId: null, // Clear any existing timeout
      },
    });
  },

  setShowLoading: (storyboardId: string, showLoading: boolean) => {
    const state = get();
    if (state.applicationStoryboards[storyboardId]) {
      set((state) => ({
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [storyboardId]: {
            ...state.applicationStoryboards[storyboardId],
            showManualLoading: showLoading,
          },
        },
      }));
      return;
    }

    if (state.registeredStoryboards[storyboardId]) {
      set((state) => ({
        registeredStoryboards: {
          ...state.registeredStoryboards,
          [storyboardId]: {
            ...state.registeredStoryboards[storyboardId],
            showManualLoading: showLoading,
          },
        },
      }));
    }
  },

  _processNewHtml: async ({
    canvasId,
    storyboard,
    shellHtml,
    bodyHtml,
    navTreeFromIframe,
    skipUpdateNavTree = false,
  }: {
    canvasId: string;
    storyboard: Storyboard;
    shellHtml: string;
    bodyHtml: string;
    navTreeFromIframe?: NavTreeNode;
    skipUpdateNavTree?: boolean;
  }): Promise<{
    isEmpty?: boolean;
  }> => {
    const storyboardId = storyboard?.id;
    const state = get();

    // Early return - if the shell and body are the same as the previous, return
    if (
      shellHtml === state.shellForStoryboard[storyboardId] &&
      bodyHtml === state.bodyHtmlForStoryboard[storyboardId]
    ) {
      return {
        isEmpty: false,
      };
    }

    // Pre-process the nav tree to filter out nodes we don't want
    let newNavTree = navTreeFromIframe;
    if (newNavTree) {
      try {
        newNavTree = filterOutNotNeededNodes(newNavTree, storyboard);
      } catch (error) {
        console.error(error);
        newNavTree = undefined;
      }
    } else {
      // If not provided, use the previous nav tree
      newNavTree = useCanvasStore.getState().storyboardToNavTree[storyboardId];
    }

    // Combine shell and body for storage and StaticIframe prop
    const combinedHtml = combineShellAndBody(shellHtml, bodyHtml);

    // Update the html for this storyboard
    set((state) => ({
      htmlForStoryboard: {
        ...state.htmlForStoryboard,
        [storyboardId]: combinedHtml,
      },
      shellForStoryboard: {
        ...state.shellForStoryboard,
        [storyboardId]: shellHtml,
      },
      bodyHtmlForStoryboard: {
        ...state.bodyHtmlForStoryboard,
        [storyboardId]: bodyHtml,
      },
    }));

    if (!newNavTree || isNavTreeEmpty(newNavTree)) {
      return {
        isEmpty: true,
      };
    }

    const newLookups = skipUpdateNavTree
      ? getEmptyLookups(storyboardId)
      : rebuildLookupsFromNavTree(newNavTree, storyboardId);

    // Save to database
    throttledUpdateSavedHtmlOnDB(
      storyboardId,
      canvasId || "",
      combinedHtml,
      newLookups.navTree,
      skipUpdateNavTree,
    );

    // Nav tree may have changed, update the lookups
    if (!skipUpdateNavTree) {
      await new Promise((resolve) => setTimeout(resolve, 0));

      updateAllStoreLookups(storyboardId, newLookups, true);
    }

    return {
      isEmpty: isNavTreeEmpty(newNavTree),
    };
  },

  shouldIgnoreUpdatesForStoryboard: (
    storyboardId: string,
    currentMaxHotReloadTs: number,
  ) => {
    const state = get();

    const serverChangeHistory =
      useDesignCommandManager.getState().serverChangeHistory;

    const changesNotReflectedInHotReload = serverChangeHistory
      .filter(
        (change) =>
          change.hotReloadTs === APPLIED_BUT_NOT_CONFIRMED_BY_API_CONSTANT ||
          (change.hotReloadTs && change.hotReloadTs > currentMaxHotReloadTs),
      )
      .filter(
        (change) =>
          change.hotReloadTs !== FAILED_CONSTANT && !change.noApiRequest,
      )
      .filter((change) => {
        let changeToCheck = change;
        if (change.type === ChangeType.UNDO) {
          changeToCheck = (change as UndoChange).changeFields.changeToUndo;
        } else if (change.type === ChangeType.REDO) {
          changeToCheck = (change as RedoChange).changeFields.changeToRedo;
        }

        return (
          changeToCheck.storyboardsAppliedTo.size > 0 &&
          changeToCheck.storyboardsAppliedTo.has(storyboardId)
        );
      });

    const ignore =
      state.ignoreUpdatesForStoryboards.has(storyboardId) ||
      (state.expectedMaxHotReloadTs || 0) > currentMaxHotReloadTs ||
      changesNotReflectedInHotReload.length > 0;

    return ignore;
  },

  handleNavTreeReceived: async (
    storyboard,
    shellHtml,
    bodyHtml,
    canvasId,
    applyInstantUpdate,
    navTree,
    overwriteHotReloadTs,
  ) => {
    await new Promise((resolve) => setTimeout(resolve, 0));
    const storyboardId = storyboard.id;

    // Mark the storyboard as finished navigating
    const state = get();

    // If the iframe is not meant to be on this storyboard, return
    const registeredSb = state.registeredStoryboards[storyboardId];
    const appSb = state.applicationStoryboards[storyboardId];
    if (!registeredSb && !appSb) {
      return;
    }

    const maxHotReloadTs = Math.max(
      state.maxHotReloadTs || 0,
      overwriteHotReloadTs || 0,
    );

    const ignoreAllUpdates = get().shouldIgnoreUpdatesForStoryboard(
      storyboardId,
      maxHotReloadTs,
    );

    set({ maxHotReloadTs: maxHotReloadTs });

    // Process the new html
    if (!ignoreAllUpdates) {
      const { isEmpty: isEmptyNavTree } = await get()._processNewHtml({
        canvasId,
        storyboard,
        shellHtml,
        bodyHtml,
        navTreeFromIframe: navTree || undefined,
        applyInstantUpdate,
        skipUpdateNavTree: false,
      });

      // For application storyboards, if the tree comes back empty wait 3 seconds then refresh the storyboard
      // (sometimes the react fiber tree gets wiped out and needs to be re-rendered)
      // If it loads in in within the 3 seconds, don't refresh
      if (
        isEmptyNavTree &&
        appSb &&
        !state.hadEmptyNavTreeStoryboardIds.has(storyboardId)
      ) {
        set((state) => ({
          applicationStoryboards: {
            ...state.applicationStoryboards,
            [storyboardId]: {
              ...appSb,
              needsFreshNavTreeTs: maxHotReloadTs,
            },
          },
        }));

        setTimeout(() => {
          const appSb = get().applicationStoryboards[storyboardId];
          if (!appSb || appSb?.needsFreshNavTreeTs !== maxHotReloadTs) return;

          set((state) => ({
            hadEmptyNavTreeStoryboardIds: new Set([
              ...state.hadEmptyNavTreeStoryboardIds,
              storyboardId,
            ]),
          }));

          // Otherwise, refresh the storyboard
          get().refreshStoryboard(storyboardId);
        }, 3 * 1000);

        return;
      } else {
        set((state) => ({
          hadEmptyNavTreeStoryboardIds: new Set(
            Array.from(state.hadEmptyNavTreeStoryboardIds).filter(
              (id) => id !== storyboardId,
            ),
          ),
        }));
      }
    }

    if (!registeredSb && !appSb) {
      return;
    }

    if (
      (registeredSb &&
        registeredSb.hotReloadTsAtLastRefresh !== maxHotReloadTs) ||
      (appSb && appSb.hotReloadTsAtLastRefresh !== maxHotReloadTs)
    ) {
      useDevServerStore.getState().clearErrorsForStoryboard(storyboardId);

      // Also clear all terminal errors
      useDevServerStore.getState().clearTerminalErrors();
    }

    // Check if this is an application storyboard
    if (appSb) {
      const gotFreshNavTree =
        !maxHotReloadTs ||
        !appSb.needsFreshNavTreeTs ||
        maxHotReloadTs >= appSb.needsFreshNavTreeTs;

      // Clear showManualLoading for application storyboard
      set((state) => ({
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [storyboardId]: {
            ...state.applicationStoryboards[storyboardId],
            showManualLoading: false,
            lastRefreshTs: Date.now(),
            hotReloadTsAtLastRefresh: maxHotReloadTs,
            failedToLoad: false,
            needsFreshNavTreeTs: gotFreshNavTree
              ? undefined
              : appSb.needsFreshNavTreeTs,
          },
        },
      }));
      return;
    }

    // Update the registered storyboard
    const gotFreshNavTree =
      !maxHotReloadTs ||
      !registeredSb.needsFreshNavTreeTs ||
      maxHotReloadTs >= registeredSb.needsFreshNavTreeTs;

    set((state) => ({
      registeredStoryboards: {
        ...state.registeredStoryboards,
        [storyboardId]: {
          ...state.registeredStoryboards[storyboardId],
          lastRefreshTs: Date.now(),
          showManualLoading: false,
          failedToLoad: false,
          hotReloadTsAtLastRefresh: maxHotReloadTs,
          needsFreshNavTreeTs: gotFreshNavTree
            ? undefined
            : registeredSb.needsFreshNavTreeTs,
        },
      },
    }));

    // Track nav tree received for initial project launch telemetry
    useTelemetryStore.getState().markNavTreeReceived({
      canvasId,
      cached: false,
    });

    await new Promise((resolve) => setTimeout(resolve, 0));
  },

  handleNewOuterHtmlOnly: (
    storyboard,
    shellHtml,
    bodyHtml,
    canvasId,
    applyInstantUpdate,
    overwriteHotReloadTs,
  ) => {
    const state = get();
    const storyboardId = storyboard.id;

    const maxHotReloadTs = Math.max(
      state.maxHotReloadTs || 0,
      overwriteHotReloadTs || 0,
    );

    const ignoreAllUpdates = get().shouldIgnoreUpdatesForStoryboard(
      storyboardId,
      maxHotReloadTs,
    );

    if (ignoreAllUpdates) {
      return;
    }

    set({ maxHotReloadTs: maxHotReloadTs });

    // Update tracking for this storyboard with the incoming hotReloadTs
    // Process the new html
    get()._processNewHtml({
      canvasId,
      storyboard,
      shellHtml,
      bodyHtml,
      applyInstantUpdate,
      skipUpdateNavTree: true,
    });
  },

  sendMessageToApplicationStoryboard: (
    storyboardId,
    type,
    payload,
    callback,
  ) => {
    const state = get();
    const { applicationStoryboardPorts } = useIframeStore.getState();

    const newMessage = {
      id: uuidv4(),
      type: type!,
      payload: payload,
      storyboardId: storyboardId,
    };

    // Check if this is an application storyboard
    const appStoryboard = state.applicationStoryboards[storyboardId];
    if (!appStoryboard) {
      console.error(
        `[IFRAME POOL STORE] No application storyboard found for storyboard id: ${storyboardId}`,
      );
      return;
    }

    // For application storyboards, send directly to their dedicated iframe
    const port = applicationStoryboardPorts[appStoryboard.iframeId];
    if (port) {
      port.postMessage(filterSerializable(newMessage));
      if (callback) callback();
    }
  },

  // Application storyboard methods
  setApplicationStoryboardContentWindow: (storyboardId, contentWindow) => {
    set((state) => {
      const appStoryboard = state.applicationStoryboards[storyboardId];
      if (!appStoryboard) return state;

      return {
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [storyboardId]: {
            ...appStoryboard,
            contentWindow,
          },
        },
      };
    });
  },

  resetApplicationStoryboard: (storyboardId) => {
    set((state) => {
      const appStoryboard = state.applicationStoryboards[storyboardId];
      if (!appStoryboard) return state;

      return {
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [storyboardId]: {
            ...appStoryboard,
            contentWindow: null,
            failedToLoad: false,
            refreshKey: appStoryboard.refreshKey + 1,
          },
        },
      };
    });
  },

  retryApplicationIframe: (storyboardId) => {
    set((state) => {
      const appStoryboard = state.applicationStoryboards[storyboardId];
      if (!appStoryboard) return state;

      // Clear heartbeat timeout if exists
      if (appStoryboard.heartbeatTimeoutId) {
        clearTimeout(appStoryboard.heartbeatTimeoutId);
      }

      return {
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [storyboardId]: {
            ...appStoryboard,
            status: "loading",
            refreshKey: appStoryboard.refreshKey + 1,
            contentWindow: null,
            isNonResponsive: false,
            heartbeatTimeoutId: null,
          },
        },
      };
    });
  },

  // Scroll position
  storyboardScrollPositions: {},
  setStoryboardScrollPosition: (storyboardId, domUniquePath, top, left) => {
    set((state) => {
      state.storyboardScrollPositions = {
        ...state.storyboardScrollPositions,
        [storyboardId]: {
          ...(state.storyboardScrollPositions[storyboardId] || {}),
          [domUniquePath]: { top, left },
        },
      };
      return state;
    });
  },

  ignoreUpdatesForStoryboards: new Set(),
  setIgnoreUpdatesForStoryboards: (storyboardId) => {
    set((state) => ({
      ignoreUpdatesForStoryboards: new Set([
        ...state.ignoreUpdatesForStoryboards,
        storyboardId,
      ]),
    }));
  },
  unsetIgnoreUpdatesForStoryboards: (storyboardId) => {
    set((state) => ({
      ignoreUpdatesForStoryboards: new Set(
        Array.from(state.ignoreUpdatesForStoryboards).filter(
          (id) => id !== storyboardId,
        ),
      ),
    }));
  },

  // Heartbeat methods
  handleHeartbeatReceived: (appStoryboardIframeId?: string) => {
    const state = get();

    // If an appStoryboardIframeId is provided, check if it's an application storyboard
    if (appStoryboardIframeId) {
      const appStoryboardEntry = Object.entries(
        state.applicationStoryboards,
      ).find(([_, appSb]) => appSb.iframeId === appStoryboardIframeId);

      if (appStoryboardEntry) {
        const [storyboardId, appStoryboard] = appStoryboardEntry;

        // Only clear non-responsive status if it's actually non-responsive
        if (appStoryboard.isNonResponsive) {
          state.clearNonResponsiveStatus(storyboardId);
        }

        // Clear existing timeout
        if (appStoryboard.heartbeatTimeoutId) {
          clearTimeout(appStoryboard.heartbeatTimeoutId);
        }

        // Only set timeout if the storyboard is actually ready/active
        let timeoutId: NodeJS.Timeout | null = null;
        if (appStoryboard.status === "ready" && appStoryboard.contentWindow) {
          timeoutId = setTimeout(() => {
            get().markStoryboardAsNonResponsive(storyboardId);
          }, IFRAME_HEARTBEAT_TIMEOUT_MS);
        }

        set((state) => ({
          applicationStoryboards: {
            ...state.applicationStoryboards,
            [storyboardId]: {
              ...state.applicationStoryboards[storyboardId],
              isNonResponsive: false,
              heartbeatTimeoutId: timeoutId,
            },
          },
        }));

        return;
      }
    }

    // Handle the main tempobook iframe heartbeat
    const tempobookIframe = state.tempobookIframe;
    if (!tempobookIframe) return;

    // Clear existing timeout
    clearHealthcheckTimeout(tempobookIframe);

    // Only set timeout if the tempobook iframe is ready
    let timeoutId: NodeJS.Timeout | null = null;
    if (tempobookIframe.status === "ready" && tempobookIframe.contentWindow) {
      timeoutId = setTimeout(() => {
        // Mark tempobook iframe as non-responsive
        set((state) => ({
          tempobookIframe: state.tempobookIframe
            ? { ...state.tempobookIframe, isNonResponsive: true }
            : null,
        }));
      }, IFRAME_HEARTBEAT_TIMEOUT_MS);
    }

    set({
      tempobookIframe: {
        ...tempobookIframe,
        isNonResponsive: false,
        heartbeatTimeoutId: timeoutId,
      },
    });
  },

  markStoryboardAsNonResponsive: (storyboardId: string) => {
    const state = get();

    // Check if this is an application storyboard
    const appStoryboard = state.applicationStoryboards[storyboardId];
    if (appStoryboard) {
      // Only send PostHog event once per canvas
      if (!state.nonResponsiveEventsSentForCanvas.has(appStoryboard.canvasId)) {
        track("STORYBOARD_NON_RESPONSIVE", {
          storyboardId,
          canvasId: appStoryboard.canvasId,
          storyboardType: "application",
        });

        set((state) => ({
          nonResponsiveEventsSentForCanvas: new Set([
            ...state.nonResponsiveEventsSentForCanvas,
            appStoryboard.canvasId,
          ]),
        }));
      }

      set((state) => ({
        applicationStoryboards: {
          ...state.applicationStoryboards,
          [storyboardId]: {
            ...state.applicationStoryboards[storyboardId],
            isNonResponsive: true,
          },
        },
      }));

      console.warn(
        `Application storyboard ${storyboardId} marked as non-responsive - possible infinite re-render or hang`,
      );
      return;
    }
  },

  clearNonResponsiveStatus: (storyboardId: string) => {
    const state = get();

    // Check if this is an application storyboard by storyboard ID
    const appStoryboard = state.applicationStoryboards[storyboardId];
    if (appStoryboard) {
      if (appStoryboard.isNonResponsive) {
        set((state) => ({
          applicationStoryboards: {
            ...state.applicationStoryboards,
            [storyboardId]: {
              ...state.applicationStoryboards[storyboardId],
              isNonResponsive: false,
            },
          },
        }));
      }
      return;
    }

    // For the main tempobook iframe
    if (state.tempobookIframe?.isNonResponsive) {
      set((state) => ({
        tempobookIframe: state.tempobookIframe
          ? { ...state.tempobookIframe, isNonResponsive: false }
          : null,
      }));
    }
  },

  setLatestHtmlShell: (htmlShell: string) => {
    set({ latestHtmlShell: htmlShell });
  },
}));
